import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  SessionCluster,
  SessionLeaf,
  SessionDetailMessage,
  Viewport,
} from '../types/charge-session.types';

/**
 * Charge-session map state (BIZOPS-1588, contract v2 — viewport clustering).
 *
 * All data here arrives exclusively via `postMessage` from the Hub
 * studio-viewer parent — this store never triggers a fetch. Modeled on
 * `ui-mode-store.ts` (`create()(devtools())`, no persist).
 *
 * The parent pushes server-clustered `clusters` + `leaves` for whatever
 * viewport the SPA last reported. At low/medium zoom `clusters` is populated and
 * `leaves` empty; at/above the leaf zoom threshold the inverse holds. Selecting
 * a leaf opens the detail panel (Tier-2); clusters are click-to-zoom and have no
 * detail.
 */
interface ChargeSessionsState {
  /** Latest H3 clusters pushed by the parent for the reported viewport. */
  clusters: SessionCluster[];
  /** Latest leaf points pushed by the parent (populated at high zoom). */
  leaves: SessionLeaf[];
  /** The zoom the server clustered for (from the latest Tier-1 push). */
  zoom: number | null;
  /** The H3 resolution the server binned at (from the latest Tier-1 push). */
  resolution: number | null;
  /** ISO timestamp of the latest Tier-1 push (from the parent). */
  generatedAt: string | null;
  /** The id of the leaf the user has opened a detail panel for, if any. */
  selectedPointId: string | null;
  /** Latest Tier-2 detail payload for the selected leaf, if received. */
  selectedDetail: SessionDetailMessage | null;
  /** True once the parent has acknowledged our handshake with a Tier-1 push. */
  hasReceivedData: boolean;
  /**
   * Debounced viewport reporter registered by the postMessage bridge
   * (`useChargeSessionMessages`) on mount. The map host (`GoogleMapView`) reads
   * it and calls it on every map `idle`/`bounds_changed` so the bridge stays the
   * single owner of the postMessage protocol while the map host owns the
   * bounds/zoom read. `null` until the bridge mounts.
   */
  sendViewport: ((viewport: Viewport) => void) | null;

  /** Bridge hook registers (or clears, with `null`) the viewport reporter. */
  registerSendViewport: (sender: ((viewport: Viewport) => void) | null) => void;
  setClusters: (payload: {
    clusters: SessionCluster[];
    leaves: SessionLeaf[];
    zoom: number;
    // `null`/absent at leaf zoom (z >= 16): the gateway has no cluster
    // resolution once it emits individual leaves. Normalized to `null` in the
    // store. Mirrors `chargeSessionsMessageSchema.resolution` (`.nullish()`).
    resolution: number | null | undefined;
    generatedAt: string;
  }) => void;
  /**
   * Open a detail panel for a leaf. Selecting a *different* leaf clears any
   * stale detail payload so the panel shows its loading state until the new
   * Tier-2 detail arrives. Re-selecting the *already-open* leaf is a no-op: its
   * loaded detail is kept, so clicking the same marker again doesn't strand the
   * panel in its loading skeleton.
   */
  selectPoint: (pointId: string) => void;
  setDetail: (detail: SessionDetailMessage) => void;
  /** Close the detail panel and clear its data. */
  clearSelection: () => void;
}

export const useChargeSessionsStore = create<ChargeSessionsState>()(
  devtools(
    (set) => ({
      clusters: [],
      leaves: [],
      zoom: null,
      resolution: null,
      generatedAt: null,
      selectedPointId: null,
      selectedDetail: null,
      hasReceivedData: false,
      sendViewport: null,
      registerSendViewport: (sender) => set({ sendViewport: sender }),
      setClusters: (payload) =>
        set({
          clusters: payload.clusters,
          leaves: payload.leaves,
          zoom: payload.zoom,
          // Collapse absent (`undefined`, leaf zoom) to the store's `null`.
          resolution: payload.resolution ?? null,
          generatedAt: payload.generatedAt,
          hasReceivedData: true,
        }),
      selectPoint: (pointId) =>
        set((state) =>
          state.selectedPointId === pointId
            ? // Already open for this leaf — keep the loaded detail, don't reset
              // to loading (the Tier-2 effect in App.tsx only re-fires when
              // selectedPointId changes).
              state
            : { selectedPointId: pointId, selectedDetail: null }
        ),
      setDetail: (detail) =>
        set({
          selectedDetail: detail,
          selectedPointId: detail.id,
        }),
      clearSelection: () =>
        set({
          selectedPointId: null,
          selectedDetail: null,
        }),
    }),
    { name: 'ChargeSessionsStore' }
  )
);
